import { useEffect, useRef } from 'react';

/**
 * Animated pixel-field overlay for the onboarding blue panel (IX-3922).
 *
 * Faithful port of the design prototype's `pixelFieldInit`: it samples a
 * hero-bg map into ~8px squares over the panel and twinkles them, so the flat
 * Rose-blue reads as a living field of pixels. The field can ease toward a
 * detected brand colour via the `tint` prop. Honours `prefers-reduced-motion`
 * by drawing a single static frame instead of animating.
 *
 * Renders an absolutely-positioned `<canvas>` sized to its (positioned)
 * parent, so drop it as the first child of the relative blue panel.
 */
const HERO_BG_SRC = '/images/onboarding-hero-bg.png';
const PIXEL = 8; // chunky, well-defined squares — matches the source
// Display-only guardrail: keep extracted primary colors raw upstream, but lift
// very dark tints here so the pixel field still reads as a textured color panel.
const MIN_TINT_LUMINANCE = 96;

// Flat-field fill: the source map is empty in the centre, so those squares
// would read as big dead-blue zones. We fill them with a coherent value-noise
// scatter — organic clumps instead of uniform speckle — so the whole panel
// stays a living field of pixels.
const FLAT_NOISE_SCALE = 7; // squares per noise cell — bigger = broader clumps
const FLAT_MIN_DENSITY = 0.38; // coverage floor everywhere — kills flat zones
const FLAT_DEV_BASE = 0.16; // faintest fill pixel (must read over the veil)
const FLAT_DEV_RANGE = 0.3; // extra brightness in the noise's high spots
// Soft ceiling on the map's own pixels so the bright edge cluster sits close in
// brightness to the flat-fill cluster — one field, not two.
const MAP_DEV_CAP = 0.5;

// Cheap 2D value noise (hash + smoothstep bilerp), fbm over 3 octaves.
const hash2 = (x: number, y: number): number => {
    const s = Math.sin(x * 127.1 + y * 311.7) * 43758.5453;
    return s - Math.floor(s);
};
const smoothstep = (t: number): number => t * t * (3 - 2 * t);
const valueNoise = (x: number, y: number): number => {
    const xi = Math.floor(x);
    const yi = Math.floor(y);
    const u = smoothstep(x - xi);
    const v = smoothstep(y - yi);
    const tl = hash2(xi, yi);
    const tr = hash2(xi + 1, yi);
    const bl = hash2(xi, yi + 1);
    const br = hash2(xi + 1, yi + 1);
    return (tl * (1 - u) + tr * u) * (1 - v) + (bl * (1 - u) + br * u) * v;
};
const fbm = (x: number, y: number): number => {
    let sum = 0;
    let amp = 0.5;
    let freq = 1;
    for (let o = 0; o < 3; o++) {
        sum += amp * valueNoise(x * freq, y * freq);
        freq *= 2;
        amp *= 0.5;
    }
    return sum / 0.875; // normalise (0.5 + 0.25 + 0.125) back to ~0..1
};

interface Pixel {
    x: number;
    y: number;
    dev: number; // "map-ness": how far this square deviates from the flat field
    wc: number; // diagonal position, drives the travelling wave
    ph: number; // per-pixel twinkle phase
}

interface PixelFieldProps {
    /** CSS colour the field eases toward, for the brand-morph effect. */
    tint?: string;
    className?: string;
}

export function PixelField({ tint, className }: PixelFieldProps) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    // Read the latest tint inside the animation loop without re-running the effect.
    const tintRef = useRef<string | undefined>(tint);
    tintRef.current = tint;

    useEffect(() => {
        const canvas = canvasRef.current;
        const parent = canvas?.parentElement;
        const ctx = canvas?.getContext('2d');
        if (!canvas || !parent || !ctx) return;

        const off = document.createElement('canvas');
        const octx = off.getContext('2d', { willReadFrequently: true });
        if (!octx) return;

        const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

        let w = 0;
        let h = 0;
        let pixels: Pixel[] = [];
        let raf = 0;
        let imgReady = false;
        // Native blue field; overwritten with the map's true average once loaded.
        let baseR = 59;
        let baseG = 130;
        let baseB = 246;
        // Brand-tint morph state.
        let tintAmt = 0;
        let tR = baseR;
        let tG = baseG;
        let tB = baseB;
        let lastTint = '';

        const parseColor = (str: string): [number, number, number] | null => {
            try {
                octx.clearRect(0, 0, 1, 1);
                octx.fillStyle = '#000';
                octx.fillStyle = str;
                octx.fillRect(0, 0, 1, 1);
                const d = octx.getImageData(0, 0, 1, 1).data;
                return [d[0], d[1], d[2]];
            } catch {
                return null;
            }
        };

        const liftDarkTint = (color: [number, number, number]): [number, number, number] => {
            const [r, g, b] = color;
            const lum = 0.299 * r + 0.587 * g + 0.114 * b;
            if (lum >= MIN_TINT_LUMINANCE) return color;

            const mix = Math.min(0.42, (MIN_TINT_LUMINANCE - lum) / 180);
            return [r + (255 - r) * mix, g + (255 - g) * mix, b + (255 - b) * mix];
        };

        const img = new Image();

        const build = () => {
            const rect = parent.getBoundingClientRect();
            if (!rect.width || !rect.height || !imgReady) return;
            const dpr = Math.min(window.devicePixelRatio || 1, 2);
            w = rect.width;
            h = rect.height;
            canvas.width = w * dpr;
            canvas.height = h * dpr;
            canvas.style.width = `${w}px`;
            canvas.style.height = `${h}px`;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

            const cols = Math.ceil(w / PIXEL);
            const rows = Math.ceil(h / PIXEL);
            off.width = cols;
            off.height = rows;
            octx.imageSmoothingEnabled = false;
            // Cover-crop the map, anchored top-left (its dense edge).
            const ir = img.width / img.height;
            const pr = cols / rows;
            let sw: number;
            let sh: number;
            if (ir > pr) {
                sh = img.height;
                sw = sh * pr;
            } else {
                sw = img.width;
                sh = sw / pr;
            }
            octx.clearRect(0, 0, cols, rows);
            octx.drawImage(img, 0, 0, sw, sh, 0, 0, cols, rows);

            const d = octx.getImageData(0, 0, cols, rows).data;
            const n = cols * rows;
            let sum = 0;
            let cnt = 0;
            let sr = 0;
            let sg = 0;
            let sb = 0;
            for (let i = 0; i < n; i++) {
                if (d[i * 4 + 3] < 8) continue;
                sr += d[i * 4];
                sg += d[i * 4 + 1];
                sb += d[i * 4 + 2];
                sum += 0.299 * d[i * 4] + 0.587 * d[i * 4 + 1] + 0.114 * d[i * 4 + 2];
                cnt++;
            }
            const avg = cnt ? sum / cnt : 0;
            if (cnt) {
                baseR = sr / cnt;
                baseG = sg / cnt;
                baseB = sb / cnt;
            }
            const diag = cols + rows || 1;
            pixels = [];
            for (let gy = 0; gy < rows; gy++) {
                for (let gx = 0; gx < cols; gx++) {
                    const i = (gy * cols + gx) * 4;
                    if (d[i + 3] < 8) continue;
                    const lum = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
                    const dev = Math.min(1, Math.abs(lum - avg) / 26);
                    if (dev < 0.06) {
                        // Flat field: fill with organic coherent-noise scatter so
                        // the map's empty centre reads as a living field of pixels
                        // rather than a big flat zone. Denser/brighter in the noise's
                        // high spots, sparser in its lows, never fully flat.
                        const nz = fbm(gx / FLAT_NOISE_SCALE, gy / FLAT_NOISE_SCALE);
                        const density = FLAT_MIN_DENSITY + (1 - FLAT_MIN_DENSITY) * nz;
                        if (Math.random() > density) continue;
                        pixels.push({
                            x: gx * PIXEL,
                            y: gy * PIXEL,
                            dev: FLAT_DEV_BASE + nz * FLAT_DEV_RANGE,
                            wc: (gx + gy) / diag,
                            ph: Math.random() * 6.283,
                        });
                        continue;
                    }
                    pixels.push({
                        x: gx * PIXEL,
                        y: gy * PIXEL,
                        dev: Math.min(dev, MAP_DEV_CAP),
                        wc: (gx + gy) / diag,
                        ph: Math.random() * 6.283,
                    });
                }
            }
        };

        const draw = (t: number) => {
            if (!canvas.isConnected) return;
            ctx.clearRect(0, 0, w, h);

            const req = tintRef.current?.trim() ?? '';
            if (req !== lastTint) {
                lastTint = req;
                const c = req ? parseColor(req) : null;
                if (c) [tR, tG, tB] = liftDarkTint(c);
            }
            const want = req ? 1 : 0;
            tintAmt += (want - tintAmt) * (reduce ? 1 : 0.09);
            const a = tintAmt < 0.001 ? 0 : tintAmt;
            const fR = baseR + (tR - baseR) * a;
            const fG = baseG + (tG - baseG) * a;
            const fB = baseB + (tB - baseB) * a;

            // Solid field first — the "uni" the map fades into.
            ctx.fillStyle = `rgb(${fR | 0},${fG | 0},${fB | 0})`;
            ctx.fillRect(0, 0, w, h);

            const tw = reduce ? 0 : t * 0.0018;
            for (const p of pixels) {
                let lit = 1;
                if (!reduce) {
                    const own = 0.5 + 0.5 * Math.sin(tw + p.ph);
                    const wave = 0.5 + 0.5 * Math.sin(tw * 0.6 - p.wc * 7);
                    lit = own * (0.45 + 0.55 * wave);
                }
                const boost = p.dev * (0.45 + 0.55 * lit) * 190;
                const r = Math.min(255, fR + boost);
                const g = Math.min(255, fG + boost);
                const b = Math.min(255, fB + boost * 1.1);
                const alpha = Math.min(1, p.dev * 1.5) * (reduce ? 0.7 : 0.3 + 0.7 * lit);
                ctx.fillStyle = `rgba(${r | 0},${g | 0},${b | 0},${alpha.toFixed(3)})`;
                ctx.fillRect(p.x, p.y, PIXEL, PIXEL);
            }

            // Light uniform veil — attenuates the whole texture evenly.
            ctx.fillStyle = `rgba(${fR | 0},${fG | 0},${fB | 0},0.32)`;
            ctx.fillRect(0, 0, w, h);

            if (!reduce) raf = requestAnimationFrame(draw);
        };

        const start = () => {
            build();
            draw(0);
        };

        img.onload = () => {
            imgReady = true;
            start();
        };
        img.src = HERO_BG_SRC;

        const ro = new ResizeObserver(() => {
            build();
            if (reduce) draw(0);
        });
        ro.observe(parent);

        return () => {
            cancelAnimationFrame(raf);
            ro.disconnect();
            img.onload = null;
        };
    }, []);

    return <canvas ref={canvasRef} className={className} />;
}
